/**
 * Enumerações e tabelas de compatibilidade da criação (Fase 5), conferidas na referência da
 * Marketing API v26.0 em 19/10/2026 (Campaign, AdSet, AdCreative, generatepreviews, Custom
 * Audience, targeting search). Ficam aqui porque interface, schemas Zod e API usam.
 */
import type { CampaignObjective } from "./campaign-enums.js";

/** Categorias especiais aceitas em `special_ad_categories` (v26.0). */
export const SPECIAL_AD_CATEGORIES = [
  "NONE",
  "CREDIT",
  "EMPLOYMENT",
  "HOUSING",
  "ISSUES_ELECTIONS_POLITICS",
  "FINANCIAL_PRODUCTS_SERVICES",
  "ONLINE_GAMBLING_AND_GAMING",
] as const;
export type SpecialAdCategory = (typeof SPECIAL_AD_CATEGORIES)[number];

export const BID_STRATEGIES = [
  "LOWEST_COST_WITHOUT_CAP",
  "LOWEST_COST_WITH_BID_CAP",
  "COST_CAP",
  "LOWEST_COST_WITH_MIN_ROAS",
] as const;
export type BidStrategy = (typeof BID_STRATEGIES)[number];

export const DESTINATION_TYPES = [
  "WEBSITE",
  "APP",
  "MESSENGER",
  "WHATSAPP",
  "INSTAGRAM_DIRECT",
  "ON_AD",
  "ON_POST",
  "ON_VIDEO",
  "ON_PAGE",
  "INSTAGRAM_PROFILE",
  "PHONE_CALL",
  "NONE",
] as const;
export type DestinationType = (typeof DESTINATION_TYPES)[number];

export const DESTINATION_TYPE_LABELS: Record<DestinationType, string> = {
  WEBSITE: "Site",
  APP: "Aplicativo",
  MESSENGER: "Messenger",
  WHATSAPP: "WhatsApp",
  INSTAGRAM_DIRECT: "Instagram Direct",
  ON_AD: "Formulário instantâneo",
  ON_POST: "Publicação",
  ON_VIDEO: "Vídeo",
  ON_PAGE: "Página",
  INSTAGRAM_PROFILE: "Perfil do Instagram",
  PHONE_CALL: "Ligação",
  NONE: "Sem destino",
};

export const OPTIMIZATION_GOALS = [
  "OFFSITE_CONVERSIONS",
  "VALUE",
  "LINK_CLICKS",
  "LANDING_PAGE_VIEWS",
  "IMPRESSIONS",
  "REACH",
  "AD_RECALL_LIFT",
  "THRUPLAY",
  "POST_ENGAGEMENT",
  "PAGE_LIKES",
  "LEAD_GENERATION",
  "QUALITY_LEAD",
  "CONVERSATIONS",
  "QUALITY_CALL",
  "APP_INSTALLS",
  "PROFILE_VISIT",
] as const;
export type OptimizationGoal = (typeof OPTIMIZATION_GOALS)[number];

export const BILLING_EVENTS = [
  "IMPRESSIONS",
  "LINK_CLICKS",
  "THRUPLAY",
  "POST_ENGAGEMENT",
] as const;
export type BillingEvent = (typeof BILLING_EVENTS)[number];

export interface DestinationOption {
  destination: DestinationType;
  goals: readonly OptimizationGoal[];
  /** O conjunto precisa de pixel + evento (`promoted_object.pixel_id`). */
  needsPixel: boolean;
  /** O conjunto precisa de página (`promoted_object.page_id`). */
  needsPage: boolean;
  /** Precisa de app (`promoted_object.application_id` + `object_store_url`). */
  needsApp: boolean;
}

/**
 * Tabela ODAX: para cada objetivo, os locais de conversão e as otimizações compatíveis
 * (resumo da tabela "Outcome-Driven Ad Experiences" da referência de AdSet).
 */
export const OBJECTIVE_DESTINATIONS: Record<CampaignObjective, readonly DestinationOption[]> = {
  OUTCOME_SALES: [
    {
      destination: "WEBSITE",
      goals: ["OFFSITE_CONVERSIONS", "VALUE", "LANDING_PAGE_VIEWS", "LINK_CLICKS"],
      needsPixel: true,
      needsPage: false,
      needsApp: false,
    },
    {
      destination: "APP",
      goals: ["OFFSITE_CONVERSIONS", "APP_INSTALLS"],
      needsPixel: false,
      needsPage: false,
      needsApp: true,
    },
    {
      destination: "MESSENGER",
      goals: ["CONVERSATIONS", "LINK_CLICKS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "WHATSAPP",
      goals: ["CONVERSATIONS", "LINK_CLICKS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
  ],
  OUTCOME_LEADS: [
    {
      destination: "WEBSITE",
      goals: ["OFFSITE_CONVERSIONS", "LANDING_PAGE_VIEWS", "LINK_CLICKS"],
      needsPixel: true,
      needsPage: false,
      needsApp: false,
    },
    {
      destination: "ON_AD",
      goals: ["LEAD_GENERATION", "QUALITY_LEAD"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "MESSENGER",
      goals: ["CONVERSATIONS", "LEAD_GENERATION"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "WHATSAPP",
      goals: ["CONVERSATIONS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "PHONE_CALL",
      goals: ["QUALITY_CALL"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
  ],
  OUTCOME_TRAFFIC: [
    {
      destination: "WEBSITE",
      goals: ["LINK_CLICKS", "LANDING_PAGE_VIEWS", "IMPRESSIONS", "REACH"],
      needsPixel: false,
      needsPage: false,
      needsApp: false,
    },
    {
      destination: "APP",
      goals: ["LINK_CLICKS"],
      needsPixel: false,
      needsPage: false,
      needsApp: true,
    },
    {
      destination: "MESSENGER",
      goals: ["LINK_CLICKS", "CONVERSATIONS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "WHATSAPP",
      goals: ["LINK_CLICKS", "CONVERSATIONS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "INSTAGRAM_PROFILE",
      goals: ["PROFILE_VISIT", "LINK_CLICKS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
  ],
  OUTCOME_ENGAGEMENT: [
    {
      destination: "ON_POST",
      goals: ["POST_ENGAGEMENT", "IMPRESSIONS", "REACH"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "ON_VIDEO",
      goals: ["THRUPLAY"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "ON_PAGE",
      goals: ["PAGE_LIKES"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "MESSENGER",
      goals: ["CONVERSATIONS"],
      needsPixel: false,
      needsPage: true,
      needsApp: false,
    },
    {
      destination: "WEBSITE",
      goals: ["OFFSITE_CONVERSIONS", "LINK_CLICKS"],
      needsPixel: true,
      needsPage: false,
      needsApp: false,
    },
  ],
  OUTCOME_AWARENESS: [
    {
      destination: "NONE",
      goals: ["REACH", "IMPRESSIONS", "AD_RECALL_LIFT", "THRUPLAY"],
      needsPixel: false,
      needsPage: false,
      needsApp: false,
    },
  ],
  OUTCOME_APP_PROMOTION: [
    {
      destination: "APP",
      goals: ["APP_INSTALLS", "OFFSITE_CONVERSIONS", "LINK_CLICKS"],
      needsPixel: false,
      needsPage: false,
      needsApp: true,
    },
  ],
};

export function destinationOption(
  objective: CampaignObjective,
  destination: string,
): DestinationOption | undefined {
  return OBJECTIVE_DESTINATIONS[objective].find((d) => d.destination === destination);
}

/** Evento de cobrança padrão para cada otimização (a Meta aceita IMPRESSIONS em quase todas). */
export function defaultBillingEvent(goal: OptimizationGoal): BillingEvent {
  if (goal === "LINK_CLICKS") return "LINK_CLICKS";
  if (goal === "THRUPLAY") return "THRUPLAY";
  if (goal === "POST_ENGAGEMENT") return "POST_ENGAGEMENT";
  return "IMPRESSIONS";
}

/** Eventos padrão do pixel (`custom_event_type`). */
export const PIXEL_EVENTS = [
  "PURCHASE",
  "LEAD",
  "COMPLETE_REGISTRATION",
  "ADD_TO_CART",
  "INITIATED_CHECKOUT",
  "ADD_PAYMENT_INFO",
  "CONTACT",
  "SCHEDULE",
  "SUBMIT_APPLICATION",
  "SUBSCRIBE",
  "START_TRIAL",
  "VIEW_CONTENT",
  "SEARCH",
  "OTHER",
] as const;
export type PixelEvent = (typeof PIXEL_EVENTS)[number];

export const PIXEL_EVENT_LABELS: Record<PixelEvent, string> = {
  PURCHASE: "Compra",
  LEAD: "Cadastro (lead)",
  COMPLETE_REGISTRATION: "Registro concluído",
  ADD_TO_CART: "Adicionar ao carrinho",
  INITIATED_CHECKOUT: "Finalização de compra iniciada",
  ADD_PAYMENT_INFO: "Adicionar dados de pagamento",
  CONTACT: "Contato",
  SCHEDULE: "Agendamento",
  SUBMIT_APPLICATION: "Envio de inscrição",
  SUBSCRIBE: "Assinatura",
  START_TRIAL: "Início de teste",
  VIEW_CONTENT: "Visualização de conteúdo",
  SEARCH: "Pesquisa",
  OTHER: "Outro (evento personalizado)",
};

/** Botões de chamada para ação (`call_to_action.type`) mais usados. */
export const CALL_TO_ACTIONS = [
  "LEARN_MORE",
  "SHOP_NOW",
  "SIGN_UP",
  "SUBSCRIBE",
  "CONTACT_US",
  "GET_QUOTE",
  "GET_OFFER",
  "APPLY_NOW",
  "BOOK_TRAVEL",
  "DOWNLOAD",
  "ORDER_NOW",
  "WHATSAPP_MESSAGE",
  "SEND_MESSAGE",
  "CALL_NOW",
  "WATCH_MORE",
  "INSTALL_MOBILE_APP",
  "NO_BUTTON",
] as const;
export type CallToAction = (typeof CALL_TO_ACTIONS)[number];

export const CALL_TO_ACTION_LABELS: Record<CallToAction, string> = {
  LEARN_MORE: "Saiba mais",
  SHOP_NOW: "Comprar agora",
  SIGN_UP: "Cadastre-se",
  SUBSCRIBE: "Assinar",
  CONTACT_US: "Fale conosco",
  GET_QUOTE: "Solicitar orçamento",
  GET_OFFER: "Obter oferta",
  APPLY_NOW: "Inscreva-se",
  BOOK_TRAVEL: "Reservar",
  DOWNLOAD: "Baixar",
  ORDER_NOW: "Pedir agora",
  WHATSAPP_MESSAGE: "Enviar mensagem no WhatsApp",
  SEND_MESSAGE: "Enviar mensagem",
  CALL_NOW: "Ligar agora",
  WATCH_MORE: "Assistir mais",
  INSTALL_MOBILE_APP: "Instalar app",
  NO_BUTTON: "Sem botão",
};

export const AD_FORMATS = ["imagem", "video", "carrossel"] as const;
export type AdFormat = (typeof AD_FORMATS)[number];
export const AD_FORMAT_LABELS: Record<AdFormat, string> = {
  imagem: "Imagem única",
  video: "Vídeo",
  carrossel: "Carrossel (2 a 10 cartões)",
};
export const CAROUSEL_MIN_CARDS = 2;
export const CAROUSEL_MAX_CARDS = 10;

/** Posicionamentos (`publisher_platforms` e posições por plataforma). */
export const PUBLISHER_PLATFORMS = [
  "facebook",
  "instagram",
  "messenger",
  "audience_network",
] as const;
export type PublisherPlatform = (typeof PUBLISHER_PLATFORMS)[number];
export const PUBLISHER_PLATFORM_LABELS: Record<PublisherPlatform, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  messenger: "Messenger",
  audience_network: "Audience Network",
};

export const PLACEMENT_POSITIONS: Record<
  PublisherPlatform,
  readonly { id: string; label: string }[]
> = {
  facebook: [
    { id: "feed", label: "Feed" },
    { id: "video_feeds", label: "Feeds de vídeo" },
    { id: "marketplace", label: "Marketplace" },
    { id: "story", label: "Stories" },
    { id: "facebook_reels", label: "Reels" },
    { id: "right_hand_column", label: "Coluna da direita" },
    { id: "search", label: "Resultados de pesquisa" },
    { id: "instream_video", label: "Vídeos in-stream" },
  ],
  instagram: [
    { id: "stream", label: "Feed" },
    { id: "story", label: "Stories" },
    { id: "reels", label: "Reels" },
    { id: "explore", label: "Explorar" },
    { id: "explore_home", label: "Página inicial do Explorar" },
    { id: "profile_feed", label: "Feed do perfil" },
  ],
  messenger: [
    { id: "messenger_home", label: "Caixa de entrada" },
    { id: "story", label: "Stories" },
  ],
  audience_network: [
    { id: "classic", label: "Nativo, banner e intersticial" },
    { id: "rewarded_video", label: "Vídeo com recompensa" },
  ],
};

/** Formatos de prévia aceitos por `generatepreviews` (`ad_format`). */
export const PREVIEW_FORMATS = [
  { id: "MOBILE_FEED_STANDARD", label: "Feed do Facebook (celular)" },
  { id: "DESKTOP_FEED_STANDARD", label: "Feed do Facebook (computador)" },
  { id: "INSTAGRAM_STANDARD", label: "Feed do Instagram" },
  { id: "INSTAGRAM_STORY", label: "Stories do Instagram" },
  { id: "INSTAGRAM_REELS", label: "Reels do Instagram" },
  { id: "FACEBOOK_STORY_MOBILE", label: "Stories do Facebook" },
  { id: "FACEBOOK_REELS_MOBILE", label: "Reels do Facebook" },
  { id: "RIGHT_COLUMN_STANDARD", label: "Coluna da direita" },
  { id: "AUDIENCE_NETWORK_OUTSTREAM_VIDEO", label: "Audience Network" },
] as const;
export type PreviewFormat = (typeof PREVIEW_FORMATS)[number]["id"];

export const GENDERS = [
  { id: 0, label: "Todos" },
  { id: 1, label: "Homens" },
  { id: 2, label: "Mulheres" },
] as const;

/** Países mais usados pelos clientes das agências brasileiras. */
export const TARGET_COUNTRIES = [
  { code: "BR", name: "Brasil" },
  { code: "US", name: "Estados Unidos" },
  { code: "PT", name: "Portugal" },
  { code: "AR", name: "Argentina" },
  { code: "MX", name: "México" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colômbia" },
  { code: "PE", name: "Peru" },
  { code: "ES", name: "Espanha" },
  { code: "GB", name: "Reino Unido" },
  { code: "CA", name: "Canadá" },
  { code: "DE", name: "Alemanha" },
  { code: "FR", name: "França" },
  { code: "IT", name: "Itália" },
] as const;

// ---- Públicos -------------------------------------------------------------------------------------

export const AUDIENCE_KINDS = ["personalizado", "semelhante", "salvo"] as const;
export type AudienceKind = (typeof AUDIENCE_KINDS)[number];
export const AUDIENCE_KIND_LABELS: Record<AudienceKind, string> = {
  personalizado: "Personalizado",
  semelhante: "Semelhante (lookalike)",
  salvo: "Salvo",
};

export const CUSTOM_AUDIENCE_SOURCES = ["lista", "site", "engajamento"] as const;
export type CustomAudienceSource = (typeof CUSTOM_AUDIENCE_SOURCES)[number];
export const CUSTOM_AUDIENCE_SOURCE_LABELS: Record<CustomAudienceSource, string> = {
  lista: "Lista de clientes",
  site: "Site (pixel)",
  engajamento: "Engajamento",
};

export const ENGAGEMENT_SOURCES = ["pagina", "instagram", "video", "formulario"] as const;
export type EngagementSource = (typeof ENGAGEMENT_SOURCES)[number];
export const ENGAGEMENT_SOURCE_LABELS: Record<EngagementSource, string> = {
  pagina: "Página do Facebook",
  instagram: "Perfil do Instagram",
  video: "Vídeo",
  formulario: "Formulário de cadastro",
};

export const WEBSITE_RULE_OPERATORS = [
  { id: "i_contains", label: "contém" },
  { id: "i_not_contains", label: "não contém" },
  { id: "eq", label: "é igual a" },
] as const;

/** Subtipos devolvidos pela Meta em `customaudiences` → tipo do produto. */
export function audienceKindOf(subtype: string | null | undefined): AudienceKind {
  if (subtype === "LOOKALIKE") return "semelhante";
  if (subtype === "SAVED_AUDIENCE") return "salvo";
  return "personalizado";
}

export const AUDIENCE_SUBTYPE_LABELS: Record<string, string> = {
  CUSTOM: "Lista de clientes",
  WEBSITE: "Site",
  ENGAGEMENT: "Engajamento",
  LOOKALIKE: "Semelhante",
  SAVED_AUDIENCE: "Salvo",
  APP: "Aplicativo",
  VIDEO: "Vídeo",
  IG_BUSINESS: "Instagram",
  FB_EVENT: "Evento",
  OFFLINE_CONVERSION: "Eventos offline",
  CLAIM: "Reivindicação",
  MULTI_DATA: "Várias fontes",
};

/** `operation_status`/`delivery_status` traduzidos (códigos da referência Custom Audience). */
export function audienceStatusInfo(
  operation: { code?: number; description?: string } | null | undefined,
  delivery: { code?: number; description?: string } | null | undefined,
): { label: string; tone: "ok" | "atencao" | "erro" | "neutro"; reason: string | null } {
  const d = delivery?.code;
  const o = operation?.code;
  if (o === 441)
    return {
      label: "Preenchendo",
      tone: "atencao",
      reason: "A Meta ainda está populando o público.",
    };
  if (o === 471)
    return {
      label: "Sinalizado",
      tone: "erro",
      reason: "A Meta sinalizou o público por sugerir condição de saúde ou situação financeira.",
    };
  if (o !== undefined && o >= 500)
    return {
      label: "Erro",
      tone: "erro",
      reason: operation?.description ?? "Erro na última operação.",
    };
  if (d === 300)
    return {
      label: "Pequeno demais",
      tone: "atencao",
      reason: "Menos de 100 pessoas: inativo até crescer.",
    };
  if (d !== undefined && d >= 400)
    return {
      label: "Indisponível",
      tone: "erro",
      reason: delivery?.description ?? "O público não pode ser usado.",
    };
  if (d === 200 || o === 200 || (o === undefined && d === undefined))
    return { label: "Pronto", tone: "ok", reason: null };
  return {
    label: "Atenção",
    tone: "atencao",
    reason: operation?.description ?? delivery?.description ?? null,
  };
}

// ---- Criativos ----------------------------------------------------------------------------------

export const ASSET_KINDS = ["imagem", "video"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export const ASSET_STATUSES = ["enviando", "pronto", "erro"] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

/** Estado do envio de um criativo para uma conta de anúncio. */
export const ASSET_UPLOAD_STATUSES = ["enviando", "processando", "pronto", "erro"] as const;
export type AssetUploadStatus = (typeof ASSET_UPLOAD_STATUSES)[number];

export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"] as const;
export const VIDEO_MIME_TYPES = ["video/mp4", "video/quicktime", "video/webm"] as const;

// ---- Rascunhos, publicação e templates ---------------------------------------------------------

export const DRAFT_KINDS = ["assistente", "massa", "duplicacao"] as const;
export type DraftKind = (typeof DRAFT_KINDS)[number];

export const DRAFT_STATUSES = ["rascunho", "publicando", "publicado", "erro"] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

export const WIZARD_STEPS = ["campanha", "conjunto", "anuncio", "revisao"] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];
export const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  campanha: "Campanha",
  conjunto: "Conjunto de anúncios",
  anuncio: "Anúncio",
  revisao: "Revisão e publicação",
};

export const PUBLICATION_STATUSES = ["executando", "concluida", "falhou", "revertida"] as const;
export type PublicationStatus = (typeof PUBLICATION_STATUSES)[number];

/** Limite de segurança da criação em massa (anúncios por publicação). */
export const BULK_MAX_ADS = 50;

/** Padrão de nome da criação em massa (tokens entre chaves). */
export const BULK_NAME_TOKENS = ["campanha", "publico", "criativo", "texto", "n"] as const;
export const DEFAULT_ADSET_NAME_PATTERN = "{campanha} · {publico}";
export const DEFAULT_AD_NAME_PATTERN = "{criativo} · {texto}";
